import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const SocietyRulesPage = () => {
  const [tocItems, setTocItems] = useState([]);
  const [contentHtml, setContentHtml] = useState('');
  const [embeddedStyle, setEmbeddedStyle] = useState('');
  const [error, setError] = useState('');
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const pageStyles = useMemo(() => {
    return `
      .byelaws-article .page { page-break-after: auto !important; }
      .byelaws-article { max-width: none; margin: 0; }
      .byelaws-page-header { position: sticky; top: 0; z-index: 50; background: #ffffff; }
      .byelaws-toc { position: sticky; top: calc(var(--byelaws-header-height, 140px) + 12px); height: calc(100vh - var(--byelaws-header-height, 140px) - 24px); display: flex; flex-direction: column; }
      .byelaws-toc__list { flex: 1 1 auto; overflow: auto; }
      .byelaws-content { scroll-margin-top: calc(var(--byelaws-header-height, 140px) + 16px); }
    `;
  }, []);

  useEffect(() => {
    if (!headerRef.current) return;

    const el = headerRef.current;
    const update = () => setHeaderHeight(el.offsetHeight || 0);
    update();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => update());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch('/society-rules.html', { cache: 'no-cache' });
        if (!res.ok) throw new Error(`Failed to load society rules: ${res.status}`);
        const text = await res.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');

        doc.querySelectorAll('script').forEach((el) => el.remove());

        const styleTags = Array.from(doc.querySelectorAll('head style'));
        const combinedStyle = styleTags.map((s) => s.textContent || '').join('\n');

        const indexTable = doc.querySelector('table.index-table');
        const rows = indexTable ? Array.from(indexTable.querySelectorAll('tr')) : [];

        const items = rows
          .map((tr) => {
            const tds = tr.querySelectorAll('td');
            if (tds.length < 1) return null;
            const raw = (tds[0].textContent || '').replace(/\s+/g, ' ').trim();
            if (!raw) return null;

            const displayTitle = raw;
            const searchTitle = raw.replace(/^\s*\d+\.?\s*/, '').trim();

            return { displayTitle, searchTitle };
          })
          .filter(Boolean);

        function slugify(s) {
          return String(s)
            .toLowerCase()
            .replace(/&/g, 'and')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '')
            .slice(0, 80);
        }

        const toc = items.map((it, idx) => ({
          ...it,
          id: `toc-${idx + 1}-${slugify(it.displayTitle) || idx + 1}`
        }));

        const body = doc.body;

        if (indexTable) indexTable.remove();

        function normalize(s) {
          return String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
        }

        function findFirstTextNode(query) {
          const q = normalize(query);
          if (!q) return null;

          const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
            acceptNode(node) {
              const t = normalize(node.nodeValue);
              if (!t) return NodeFilter.FILTER_REJECT;
              return t.includes(q) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
            }
          });

          return walker.nextNode();
        }

        function pickAnchorElementFromNode(textNode) {
          if (!textNode) return null;
          const el = textNode.parentElement;
          if (!el) return null;
          return el.closest('h1, h2, h3, p, div') || el;
        }

        toc.forEach((it) => {
          const node = findFirstTextNode(it.searchTitle || it.displayTitle);
          const anchorEl = pickAnchorElementFromNode(node);
          if (anchorEl && !anchorEl.id) {
            anchorEl.id = it.id;
          }
        });

        if (!cancelled) {
          setTocItems(toc);
          setEmbeddedStyle(combinedStyle);
          setContentHtml(body.innerHTML);
          setError('');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load society rules');
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const onTocClick = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className="w-full"
      style={{ '--byelaws-header-height': `${headerHeight}px` }}
    >
      <div ref={headerRef} className="byelaws-page-header px-3 sm:px-4 lg:px-6 py-4 border-b border-gray-200">
        <div className="flex justify-start mb-4">
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-800 flex items-center bg-white py-2 px-4 rounded-full shadow-md hover:shadow-lg transition-all duration-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back to Home
          </Link>
        </div>

        <div className="text-center">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700 mb-3">
            Society Rules
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Bye-laws and rules for Aasra Sapna Enclave Flats Residents Welfare Society.
          </p>
        </div>
      </div>

      <div className="px-3 sm:px-4 lg:px-6 py-4">

      <style>{embeddedStyle}</style>
      <style>{pageStyles}</style>

      {error ? (
        <div className="bg-white rounded-xl shadow-lg border border-red-200 p-6 text-red-700">
          {error}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <aside className="lg:col-span-3">
            <div className="byelaws-toc bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden">
              <div className="px-4 py-3 font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-700">
                Index
              </div>
              <div className="byelaws-toc__list p-3">
                {tocItems.length ? (
                  tocItems.map((it) => (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onTocClick(it.id)}
                      className="w-full text-left px-3 py-2 mb-2 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-sm"
                    >
                      {it.displayTitle}
                    </button>
                  ))
                ) : (
                  <div className="text-sm text-gray-600">Loading index…</div>
                )}
              </div>
            </div>
          </aside>

          <main className="lg:col-span-9">
            <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-4 sm:p-6">
              <article
                className="byelaws-article byelaws-content"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            </div>
          </main>
        </div>
      )}
      </div>
    </div>
  );
};

export default SocietyRulesPage;
